import React, { useEffect, useState } from "react";
import { MODES, RETIRED_SYSTEM_TYPES, listText, typeLabel, typesIn } from "./constants";

// The API's own check for the NFC tag link: https, no spaces, query or fragment.
const TAG_LINK = /^https:\/\/[^\s?#]+$/;

// One on/off switch with its name and what it does; the whole row toggles.
const SwitchRow = ({ name, description, checked, disabled, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className="hx-adm-switch"
    disabled={disabled}
    onClick={onToggle}
  >
    <span className="hx-adm-switch-text">
      <span className="hx-adm-switch-name">{name}</span>
      <span className="hx-adm-note">{description}</span>
    </span>
    <span className={`hx-switch ${checked ? "is-on" : ""}`} aria-hidden="true">
      <span className="hx-switch-knob" />
    </span>
  </button>
);

// The feature's switches: on for every home, notifications, the NFC tag link,
// and which system runs each type. Every change goes to onChange (field, value),
// which confirms the risky ones and saves. What the API no longer lets change
// since the old system's retirement stays as it is: on for every home, and
// the retired types on New.
const Switches = ({ settings, disabled, onChange }) => {
  const [tagLink, setTagLink] = useState(settings.tagLinkBase);

  useEffect(() => {
    setTagLink(settings.tagLinkBase);
  }, [settings.tagLinkBase]);

  const link = tagLink.trim();
  const linkValid = link === "" || (link.length <= 200 && TAG_LINK.test(link));
  const types = typesIn(settings.types);
  const retired = types.filter(({ key }) => RETIRED_SYSTEM_TYPES.includes(key));

  const saveLink = (event) => {
    event.preventDefault();
    if (linkValid && link !== settings.tagLinkBase) onChange("tagLinkBase", link);
  };

  return (
    <>
      <SwitchRow
        name="On for every home"
        description={
          settings.enabled
            ? "Every home has Automation. It stays on: the old system it would hand back to is retired."
            : "On: every home has Automation. Off: only the test homes have it."
        }
        checked={settings.enabled}
        disabled={disabled || settings.enabled}
        onToggle={() => onChange("enabled", !settings.enabled)}
      />
      <SwitchRow
        name="Automation notifications"
        description="Automations can send push notifications."
        checked={settings.notificationsEnabled}
        disabled={disabled}
        onToggle={() => onChange("notificationsEnabled", !settings.notificationsEnabled)}
      />

      <form className="hx-adm-field" onSubmit={saveLink} noValidate>
        <label className="hx-label" htmlFor="hx-adm-tag-link">
          NFC tag link
        </label>
        <div className="hx-adm-inline">
          <input
            id="hx-adm-tag-link"
            className="hx-input"
            inputMode="url"
            placeholder="https://…/t/"
            autoComplete="off"
            spellCheck={false}
            value={tagLink}
            disabled={disabled}
            aria-invalid={!linkValid || undefined}
            aria-describedby="hx-adm-tag-link-note"
            onChange={(event) => setTagLink(event.target.value)}
          />
          <button type="submit" className="hx-btn" disabled={disabled || !linkValid || link === settings.tagLinkBase}>
            Save
          </button>
        </div>
        <p id="hx-adm-tag-link-note" className={`hx-adm-note ${linkValid ? "" : "is-error"}`}>
          {linkValid
            ? "The start of the link written on NFC tags. Leave it empty until the tag domain is chosen."
            : "Use an https link without spaces, ? or #, or leave it empty."}
        </p>
      </form>

      <h3 className="hx-adm-subtitle">Who runs each type</h3>
      <dl className="hx-adm-modes">
        {MODES.map((mode) => (
          <div key={mode.key}>
            <dt>{mode.label}</dt>
            <dd>{mode.help}</dd>
          </div>
        ))}
      </dl>
      {retired.length ? (
        <p className="hx-adm-note">
          {listText(retired.map(({ key }) => typeLabel(key)))} stay on New: their old system was retired on 14 Oct 2026.
        </p>
      ) : null}
      <ul className="hx-adm-types">
        {types.map(({ key, label }) => (
          <li key={key}>
            <span className="hx-adm-type">
              <span className="hx-adm-type-name">{label}</span>
              <span className="hx-adm-id">{key}</span>
            </span>
            <div className="hx-adm-seg" role="radiogroup" aria-label={`${label}: who runs it`}>
              {MODES.map((mode) => {
                const active = settings.types[key] === mode.key;
                const locked = RETIRED_SYSTEM_TYPES.includes(key) && mode.key !== "new";
                return (
                  <label
                    key={mode.key}
                    className={`is-${mode.key} ${active ? "is-active" : ""} ${locked ? "is-locked" : ""}`}
                  >
                    <input
                      type="radio"
                      name={`hx-adm-type-${key}`}
                      value={mode.key}
                      checked={active}
                      disabled={disabled || locked}
                      onChange={() => onChange("type", { key, label, mode: mode.key })}
                    />
                    <span>{mode.label}</span>
                  </label>
                );
              })}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

export default Switches;
